/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/store';
import userClient from '../../utils/apiClients/userClient';
import { ArtStyleUser } from '../../types/ArtStyles';
import { setProfileDetails } from '../../store/actions/profileActions';
import { Account, initialAccount } from './SignupContext';
import { subscriptionAction } from '../../store/slices/subscriptionSlice';
import useAuthToken from '../../hooks/useAuthToken';

export const userModes = ['creator', 'influencer'];

interface User {
  fullName: string;
  email: string;
  mobile?: string;
  profileImage: string;
}

export type UserType = 'creator' | 'influencer' | 'artist';
export type AccountType = 'personal' | 'business' | 'agency' | 'brand' | 'creative_agency';
export type LicenseID = 'personal' | 'standard' | 'commercial';
export type AppMode = UserType;

export interface SocialDetails {
  followers: number;
  handle: string;
}

export interface App {
  mode: AppMode | null;
  user: User;
}

interface AppContextProps {
  app: App | null;
  setApp: React.Dispatch<React.SetStateAction<App | null>>;
  account: Account | null;
  setAccount: React.Dispatch<React.SetStateAction<Account | null>>;
  userInfo: ArtStyleUser | null;
  setActiveProfile: (profile: UserType) => void;
  setUserInfo: (type: ArtStyleUser | null) => void;
  loading: boolean;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const getActiveProfile = (): UserType | null => {
  try {
    const user = sessionStorage.getItem('activeProfile') as UserType | null;
    const userTypes: UserType[] = ['creator', 'influencer', 'artist'];

    if (!userTypes.includes(user as UserType)) {
      const path = window.location.pathname;
      const extractedType = path.split('/')[1] as UserType;

      if (userTypes.includes(extractedType)) {
        sessionStorage.setItem('activeProfile', extractedType);
        return extractedType;
      }
      return null;
    }

    return user;
  } catch (error) {
    console.error('Error retrieving active profile:', error);
    return null;
  }
};


export const setActiveProfile = (profile: UserType) => {
  try {
    sessionStorage.setItem('activeProfile', profile);
  } catch (error) {
    console.error('Error storing active profile:', error);
  }
};

export const AppProvider: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { profile } = useAppSelector(e => e.profile);

  const query = new URLSearchParams(location.search);
  const userType = query.get('userType') as UserType | null;

  const [app, setApp] = useState<App | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [userInfo, setUserInfo] = useState<ArtStyleUser | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { isAuthenticated } = useAuthToken();

  const loadUser = async () => {
    try {
      setLoading(true);
      const { data } = await userClient.get('/users/me');
      setUserInfo(data);
    } catch (error) {
      console.error('error occured during get /users/me api call', error);
    } finally {
      setLoading(false);
    }
  };

  const setActiveProfile = (profileType: UserType) => {
    try {
      sessionStorage.setItem('activeProfile', profileType);
      setAccount(prev =>
        prev
          ? {
            ...prev,
            userType: profileType,
          }
          : prev
      );
    } catch (error) {
      console.error('Error setting active profile in session:', error);
    }
  };

  useEffect(() => {
    setApp({
      mode: getActiveProfile(),
      user: {
        ...userInfo,
        fullName: userInfo?.givenName || '',
        profileImage: userInfo?.picture || ''
      }
    });
  }, []);



  useEffect(() => {
    if (!userType) return;

    const userProfile = profile?.[userType] as unknown as Account | undefined;

    setAccount({
      ...initialAccount,
      accountName: userProfile?.accountName ?? '',
      ...userProfile,
      accountType: userProfile?.accountType as AccountType,
      userType: userType
    });
  }, [profile, userType]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setProfileDetails());
      dispatch(subscriptionAction());
      loadUser();
    }
  }, [dispatch, isAuthenticated]);

  return (
    <AppContext.Provider
      value={{
        app,
        setApp,
        account,
        setAccount,
        userInfo,
        setUserInfo,
        setActiveProfile,
        loading,
      }}
    >
      <Outlet />
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextProps => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
